import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { 
  TrendingUp, 
  Coins, 
  DollarSign, 
  Zap,
  ArrowLeft,
  Calculator,
  PiggyBank,
  Repeat,
  ShoppingCart,
  Trophy
} from 'lucide-react';

const HowToEarn = () => {
  const navigate = useNavigate();
  const [stakeAmount, setStakeAmount] = useState(1000);
  const [timeframe, setTimeframe] = useState(12); // months

  // Calculate earnings
  const stakingAPY = 5; // 5%
  const yearlyEarnings = stakeAmount * (stakingAPY / 100);
  const monthlyEarnings = yearlyEarnings / 12;
  const customEarnings = (yearlyEarnings / 12) * timeframe;

  const earningMethods = [
    {
      title: "Stake BETH Tokens",
      icon: <PiggyBank className="h-12 w-12 text-[#10b981]" />,
      difficulty: "Easy",
      potential: "Steady",
      apy: "5% APY",
      description: "Lock your BETH tokens and earn passive rewards automatically",
      features: [
        "Earn 5% annual returns",
        "Rewards paid continuously",
        "Unstake anytime (small fee if < 30 days)",
        "No active management needed"
      ],
      example: "Stake 10,000 BETH → Earn 500 BETH/year → ~42 BETH/month",
      color: "from-green-500/10 to-green-600/5",
      border: "border-green-500"
    },
    {
      title: "Borrow & Invest",
      icon: <Repeat className="h-12 w-12 text-[#3b82f6]" />,
      difficulty: "Medium",
      potential: "High",
      apy: "Variable",
      description: "Use your crypto as collateral to borrow BUSD and invest elsewhere",
      features: [
        "Deposit ETH, WETH, or BETH as collateral",
        "Borrow up to 66% of collateral value",
        "Keep your crypto exposure",
        "Use borrowed funds for other opportunities"
      ],
      example: "Deposit $10,000 BETH → Borrow $6,600 BUSD → Invest in yield farming",
      color: "from-blue-500/10 to-blue-600/5",
      border: "border-blue-500"
    },
    {
      title: "Deploy & Sell Tokens",
      icon: <ShoppingCart className="h-12 w-12 text-[#f59e0b]" />,
      difficulty: "Medium",
      potential: "Very High",
      apy: "Unlimited",
      description: "Create your own ERC20 token and sell to investors or community",
      features: [
        "Deploy custom tokens easily",
        "List on decentralized exchanges",
        "Sell to your community",
        "Build your own tokenomics"
      ],
      example: "Deploy 1M tokens → Sell at $0.10 each → Earn $100,000",
      color: "from-orange-500/10 to-orange-600/5",
      border: "border-orange-500"
    },
    {
      title: "Use Tokens as Collateral",
      icon: <Coins className="h-12 w-12 text-[#8b5cf6]" />,
      difficulty: "Advanced",
      potential: "High",
      apy: "Flexible",
      description: "Deposit your deployed tokens as collateral to unlock liquidity",
      features: [
        "Use YOUR tokens as collateral",
        "Get instant liquidity",
        "No need to sell tokens",
        "Maintain ownership & upside"
      ],
      example: "Deposit 100K of your tokens → Borrow $50,000 → Keep token exposure",
      color: "from-purple-500/10 to-purple-600/5",
      border: "border-purple-500"
    },
    {
      title: "Apply Leverage",
      icon: <TrendingUp className="h-12 w-12 text-[#ef4444]" />,
      difficulty: "Advanced",
      potential: "Very High",
      apy: "2-3x Returns",
      description: "Amplify your positions with leverage (Pro/Premium tiers only)",
      features: [
        "2x leverage with Pro ($29/mo)",
        "3x leverage with Premium ($99/mo)",
        "Multiply your gains",
        "Higher risk, higher reward"
      ],
      example: "$10,000 position + 2x leverage → Control $20,000 → Double gains (or losses)",
      color: "from-red-500/10 to-red-600/5",
      border: "border-red-500"
    },
    {
      title: "List on Token Marketplace",
      icon: <Trophy className="h-12 w-12 text-[#ec4899]" />,
      difficulty: "Easy",
      potential: "Medium",
      apy: "Variable",
      description: "Sell your deployed tokens on our built-in marketplace",
      features: [
        "Simple listing process",
        "Platform handles escrow",
        "Accept ETH or BETH payments",
        "2.5% platform fee"
      ],
      example: "List tokens → Someone buys → Instant payment → Easy!",
      color: "from-pink-500/10 to-pink-600/5",
      border: "border-pink-500"
    }
  ];

  return (
    <div className="min-h-screen bg-[#050505] text-white">
      {/* Navigation */}
      <nav className="glass border-b border-border sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-4 flex justify-between items-center">
          <h1 className="text-2xl font-bold">Bethereum</h1>
          <Button 
            variant="ghost" 
            onClick={() => navigate('/dashboard')}
          >
            <ArrowLeft className="h-4 w-4 mr-2" /> Back to Dashboard
          </Button>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-6 md:px-12 py-12">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="inline-block p-3 bg-[#10b981]/10 rounded-full mb-4">
            <DollarSign className="h-12 w-12 text-[#10b981]" />
          </div>
          <h2 className="text-4xl font-bold mb-4">How to Earn with Bethereum</h2>
          <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
            Multiple ways to earn passive income, generate yield, and grow your crypto portfolio
          </p>
        </div>

        {/* Earnings Calculator */}
        <Card className="glass border-[#10b981] mb-12 max-w-3xl mx-auto">
          <CardHeader>
            <div className="flex items-center gap-3">
              <Calculator className="h-8 w-8 text-[#10b981]" />
              <div>
                <CardTitle>Staking Rewards Calculator</CardTitle>
                <CardDescription>See how much you can earn by staking BETH</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium mb-2">Amount to Stake (BETH)</label>
                <Input
                  type="number"
                  value={stakeAmount}
                  onChange={(e) => setStakeAmount(Number(e.target.value))}
                  className="text-lg"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Time Period (months)</label>
                <Input
                  type="number"
                  value={timeframe}
                  onChange={(e) => setTimeframe(Number(e.target.value))}
                  className="text-lg"
                />
              </div>
            </div>

            <div className="grid md:grid-cols-3 gap-4 p-4 bg-secondary rounded-lg">
              <div className="text-center">
                <p className="text-sm text-muted-foreground mb-1">Monthly Earnings</p>
                <p className="text-2xl font-bold text-[#10b981]">{monthlyEarnings.toFixed(2)} BETH</p>
              </div>
              <div className="text-center">
                <p className="text-sm text-muted-foreground mb-1">Yearly Earnings</p>
                <p className="text-2xl font-bold text-[#10b981]">{yearlyEarnings.toFixed(2)} BETH</p>
              </div>
              <div className="text-center">
                <p className="text-sm text-muted-foreground mb-1">{timeframe} Month Earnings</p>
                <p className="text-2xl font-bold text-[#10b981]">{customEarnings.toFixed(2)} BETH</p>
              </div>
            </div>

            <div className="text-center">
              <p className="text-sm text-muted-foreground mb-2">Current APY: <span className="text-[#10b981] font-bold">5%</span></p>
              <Button 
                onClick={() => navigate('/defi')}
                className="bg-[#10b981] hover:bg-[#059669]"
              >
                <Zap className="h-4 w-4 mr-2" /> Start Staking Now
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Earning Methods Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
          {earningMethods.map((method, index) => (
            <Card 
              key={index}
              className={`glass border-2 ${method.border} hover:scale-105 transition-all duration-300 relative overflow-hidden`}
            >
              <div className={`absolute inset-0 bg-gradient-to-br ${method.color} opacity-50`}></div>
              
              <CardHeader className="relative">
                <div className="flex justify-between items-start mb-4">
                  <div className="text-[#10b981]">
                    {method.icon}
                  </div>
                  <div className="text-right">
                    <div className="text-xs bg-secondary px-2 py-1 rounded mb-1">{method.difficulty}</div>
                    <div className="text-xs text-[#10b981] font-semibold">{method.apy}</div>
                  </div>
                </div>
                <CardTitle className="text-xl">{method.title}</CardTitle>
                <CardDescription>{method.description}</CardDescription>
              </CardHeader>

              <CardContent className="relative space-y-4">
                <div>
                  <p className="text-sm font-semibold mb-2">Features:</p>
                  <ul className="space-y-1">
                    {method.features.map((feature, idx) => (
                      <li key={idx} className="text-xs flex items-start gap-2">
                        <span className="text-[#10b981] mt-1">•</span>
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="p-3 bg-secondary rounded-lg">
                  <p className="text-xs font-semibold mb-1">Example:</p>
                  <p className="text-xs text-muted-foreground">{method.example}</p>
                </div>

                <div className="flex items-center justify-between pt-2 border-t border-border">
                  <span className="text-xs text-muted-foreground">Potential</span>
                  <span className="text-sm font-bold text-[#10b981]">{method.potential}</span>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Quick Comparison */}
        <Card className="glass border-border">
          <CardHeader>
            <CardTitle>Quick Comparison</CardTitle>
            <CardDescription>Choose the earning method that fits your risk profile</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-border">
                    <th className="text-left p-3">Method</th>
                    <th className="text-left p-3">Risk Level</th>
                    <th className="text-left p-3">Time Commitment</th>
                    <th className="text-left p-3">Potential Returns</th>
                    <th className="text-left p-3">Best For</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-border">
                    <td className="p-3 font-semibold">Staking</td>
                    <td className="p-3"><span className="text-green-500">Low</span></td>
                    <td className="p-3">None (Passive)</td>
                    <td className="p-3">5% APY</td>
                    <td className="p-3">Beginners, passive earners</td>
                  </tr>
                  <tr className="border-b border-border">
                    <td className="p-3 font-semibold">Borrowing</td>
                    <td className="p-3"><span className="text-yellow-500">Medium</span></td>
                    <td className="p-3">Low (Occasional)</td>
                    <td className="p-3">Variable</td>
                    <td className="p-3">Liquidity seekers</td>
                  </tr>
                  <tr className="border-b border-border">
                    <td className="p-3 font-semibold">Token Sales</td>
                    <td className="p-3"><span className="text-orange-500">Medium</span></td>
                    <td className="p-3">Medium (Active)</td>
                    <td className="p-3">Unlimited</td>
                    <td className="p-3">Entrepreneurs, builders</td>
                  </tr>
                  <tr className="border-b border-border">
                    <td className="p-3 font-semibold">Collateral Use</td>
                    <td className="p-3"><span className="text-orange-500">Medium</span></td>
                    <td className="p-3">Medium</td>
                    <td className="p-3">High</td>
                    <td className="p-3">Token holders</td>
                  </tr>
                  <tr>
                    <td className="p-3 font-semibold">Leverage</td>
                    <td className="p-3"><span className="text-red-500">High</span></td>
                    <td className="p-3">High (Active)</td>
                    <td className="p-3">2-3x Returns</td>
                    <td className="p-3">Experienced traders</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>

        {/* CTA Section */}
        <div className="mt-12 text-center">
          <Card className="glass border-[#10b981] max-w-2xl mx-auto">
            <CardContent className="py-8">
              <h3 className="text-2xl font-bold mb-4">Ready to Start Earning?</h3>
              <p className="text-muted-foreground mb-6">
                Choose your earning strategy and start growing your crypto portfolio today
              </p>
              <div className="flex gap-4 justify-center flex-wrap">
                <Button 
                  onClick={() => navigate('/buy-tokens')}
                  className="bg-gradient-to-r from-[#10b981] to-[#059669] hover:from-[#059669] hover:to-[#047857]"
                >
                  <Coins className="h-4 w-4 mr-2" /> Buy BETH Tokens
                </Button>
                <Button 
                  onClick={() => navigate('/defi')}
                  className="bg-[#3b82f6] hover:bg-[#2563eb]"
                >
                  <TrendingUp className="h-4 w-4 mr-2" /> Start DeFi
                </Button>
                <Button 
                  onClick={() => navigate('/deploy')}
                  variant="outline"
                  className="border-[#8b5cf6] text-[#8b5cf6]"
                >
                  <Zap className="h-4 w-4 mr-2" /> Deploy Token
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default HowToEarn;
